import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ALL_ISO_CODES } from '../../../data/isoCodes';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import JournalForm from '../../ScientificManagement/Journal/JournalForm';
import { useCountries } from '../../../hooks/useCountries';
import '../../../css/ScientificArticleForm.css';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, itemTemplate }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label,searchKey"
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            itemTemplate={itemTemplate}
            emptyFilterMessage="Không tìm thấy kết quả"
        />
    );
};

const ScientificArticleForm = ({
    isOpen, onClose, onSubmit, formData, setFormData, isEditing,
    fields = [], articleTypes = [], users = [],
    members = [], setMembers,
    toast,
    journalTypes = [], fetchData
}) => {
    const { countries } = useCountries();

    const isReadOnlyForm = useMemo(() => {
        const currentUserStr = localStorage.getItem('user');
        const currentUserInfo = currentUserStr ? JSON.parse(currentUserStr) : null;
        const currentUserId = currentUserInfo ? Number(currentUserInfo.Id) : 0;

        const isLockedStatus = formData.Status === 'Đã gửi duyệt' || formData.Status === 'Đã duyệt';

        const isNotOwner = formData.UserId && Number(formData.UserId) !== currentUserId;

        return isLockedStatus || isNotOwner;
    }, [formData.Status, formData.UserId]);

    const [isJournalModalOpen, setIsJournalModalOpen] = useState(false);
    const [journals, setJournals] = useState([]);
    const [searchJournal, setSearchJournal] = useState('');
    const [languageOptions, setLanguageOptions] = useState([]);

    const [doiInput, setDoiInput] = useState('');
    const [isExtracting, setIsExtracting] = useState(false);

    const [previewScores, setPreviewScores] = useState(null);

    const initialJournalForm = {
        Name: '', JournalTypeId: '', ISSN: '', Score: '',
        Country: 'Việt Nam', Publisher: '', ImpactFactor: '', QCategory: '', IsHDCGS: false
    };
    const [isCreateJournalModalOpen, setIsCreateJournalModalOpen] = useState(false);
    const [journalFormData, setJournalFormData] = useState(initialJournalForm);

    const [pendingFiles, setPendingFiles] = useState([]);
    const [deletedFileIds, setDeletedFileIds] = useState([]);
    const [isSubmittingForm, setIsSubmittingForm] = useState(false);

    const journalCategories = ["SCIE", "SSCI", "AHCI", "SCOPUS", "ESCI", "Khác"];

    const fetchJournals = useCallback(() => {
        fetch(`${process.env.REACT_APP_API_BASE_URL}/journal`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } })
            .then(res => res.json())
            .then(data => setJournals(data))
            .catch(err => console.error("Lỗi tải tạp chí:", err));
    }, []);

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    useEffect(() => {
        if (isOpen) {
            setDoiInput('');
            setPreviewScores(null);
            setPendingFiles([]);
            setDeletedFileIds([]);

            const viTranslator = new Intl.DisplayNames(['vi'], { type: 'language' });

            const allLangs = ALL_ISO_CODES.map(code => {
                let nameVi = viTranslator.of(code) || code;
                nameVi = nameVi.charAt(0).toUpperCase() + nameVi.slice(1);
                return { code, name: nameVi };
            }).filter(lang => lang.name.toLowerCase() !== lang.code.toLowerCase());

            const priorityCodes = ['vi', 'en', 'fr', 'ru'];
            const priorityLangs = priorityCodes
                .map(code => allLangs.find(l => l.code === code))
                .filter(Boolean);

            const otherLangs = allLangs
                .filter(l => !priorityCodes.includes(l.code))
                .sort((a, b) => a.name.localeCompare(b.name));

            setLanguageOptions([...priorityLangs, ...otherLangs]);
            setFormData(prev => {
                if (!prev.LanguageName) {
                    return { ...prev, LanguageName: 'Tiếng Việt' };
                }
                return prev;
            });

            fetchJournals();
        }
    }, [isOpen, fetchJournals, setFormData]);

    useEffect(() => {
        if (isOpen) {
            setMembers(prevMembers => {
                if (!prevMembers || prevMembers.length === 0) return prevMembers;
                return prevMembers;
            });
        }
    }, [isOpen, setMembers]);

    const existingFiles = useMemo(() => {
        if (!formData.EvidenceFileName) return [];
        try {
            const parsed = JSON.parse(formData.EvidenceFileName);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }, [formData.EvidenceFileName]);

    const handleCreateJournalSubmit = async (e) => {
        e.preventDefault();
        if (isReadOnlyForm) return;

        const nameText = journalFormData.Name;
        if (nameText && nameText.trim().length >= 5) {
            const normalizeString = (str) => {
                if (!str) return '';
                return str.toLowerCase().trim()
                    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                    .replace(/đ/g, 'd').replace(/Đ/g, 'D')
                    .replace(/[^a-z0-9]/g, "");
            };

            const calculateSimilarity = (s, t) => {
                if (!s || !t) return 0;
                if (s === t) return 1;
                const d = Array(s.length + 1).fill(null).map(() => Array(t.length + 1).fill(null));
                for (let i = 0; i <= s.length; i++) d[i][0] = i;
                for (let j = 0; j <= t.length; j++) d[0][j] = j;
                for (let j = 1; j <= t.length; j++) {
                    for (let i = 1; i <= s.length; i++) {
                        const cost = s[i - 1] === t[j - 1] ? 0 : 1;
                        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
                    }
                }
                return 1.0 - (d[s.length][t.length] / Math.max(s.length, t.length));
            };

            const normInput = normalizeString(nameText);
            const inputIssn = journalFormData.ISSN ? journalFormData.ISSN.replace(/-/g, '').trim() : '';

            let isDuplicate = false;

            for (let item of journals) {
                const normExisting = normalizeString(item.Name);
                const sim = calculateSimilarity(normInput, normExisting);
                const existingIssn = item.ISSN ? item.ISSN.replace(/-/g, '').trim() : '';

                if (sim > 0.90) {
                    if (sim > 0.98 || (inputIssn && inputIssn === existingIssn)) {
                        isDuplicate = true;
                        toast?.current?.show({
                            severity: 'error',
                            summary: 'Lỗi trùng lặp dữ liệu',
                            detail: `Tạp chí này đã tồn tại trên hệ thống: "${item.Name}". Vui lòng kiểm tra lại!`,
                            life: 5000
                        });
                        break;
                    }
                }
            }

            if (isDuplicate) return;
        }

        const currentUserStr = localStorage.getItem('user');
        const currentUser = currentUserStr ? JSON.parse(currentUserStr) : {};

        const payload = {
            ...journalFormData,
            JournalTypeId: parseInt(journalFormData.JournalTypeId) || 0,
            Score: parseFloat(journalFormData.Score) || 0,
            CreatedBy: currentUser.Id
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/journal`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            let result;
            try {
                result = await response.json();
            } catch (e) {
                result = { status: 'error', message: 'Lỗi phản hồi từ máy chủ!' };
            }

            if (response.ok && result.status !== 'error' && result.status !== 'fail') {
                toast?.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã thêm mới tạp chí!', life: 3000 });

                fetchJournals();
                setSearchJournal(journalFormData.Name);

                setIsCreateJournalModalOpen(false);
                setJournalFormData(initialJournalForm);
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Không thể lưu',
                    detail: result.message || 'Lưu thất bại!',
                    life: 5000
                });
            }
        } catch (error) {
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    };

    const handleTitleBlur = async (e) => {
        if (isReadOnlyForm) return;
        const titleText = e.target.value;
        if (!titleText || titleText.trim().length < 5) return;

        try {
            const userStr = localStorage.getItem('user');
            const currentUser = userStr ? JSON.parse(userStr) : null;
            const userId = currentUser ? currentUser.Id : '';
            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isAdmin = currentUser?.RoleId === 1 || currentUser?.RoleId === 0 || userRoleName === 'admin' ||
                (userRoleName.includes('trưởng khoa') && currentUser?.IsDelegatedAdmin === true);

            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const response = await fetch(`${baseUrl}/scientificarticle?userId=${userId}&isAdmin=${isAdmin}`, {
                headers: {
                    'X-User-Role-Id': currentUser?.RoleId?.toString() || '',
                    'X-User-Dept-Id': currentUser?.DepartmentId?.toString() || ''
                }
            });

            if (response.ok) {
                const existingData = await response.json();

                const normalizeString = (str) => {
                    if (!str) return '';
                    return str.toLowerCase().trim()
                        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                        .replace(/đ/g, 'd').replace(/Đ/g, 'D')
                        .replace(/[^a-z0-9]/g, "");
                };

                const calculateSimilarity = (s, t) => {
                    if (!s || !t) return 0;
                    if (s === t) return 1;
                    const d = Array(s.length + 1).fill(null).map(() => Array(t.length + 1).fill(null));
                    for (let i = 0; i <= s.length; i++) d[i][0] = i;
                    for (let j = 0; j <= t.length; j++) d[0][j] = j;
                    for (let j = 1; j <= t.length; j++) {
                        for (let i = 1; i <= s.length; i++) {
                            const cost = s[i - 1] === t[j - 1] ? 0 : 1;
                            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
                        }
                    }
                    return 1.0 - (d[s.length][t.length] / Math.max(s.length, t.length));
                };

                const normInput = normalizeString(titleText);
                const currentId = isEditing ? formData.Id : 0;
                const inputYear = formData.PublicationDate ? formData.PublicationDate.substring(0, 4) : '';

                for (let item of existingData) {
                    if (item.Id === currentId) continue;

                    const normExisting = normalizeString(item.Title);
                    const sim = calculateSimilarity(normInput, normExisting);
                    const existingYear = item.PublicationDate ? item.PublicationDate.substring(0, 4) : '';

                    if (sim > 0.90) {
                        if (sim > 0.98 || existingYear === inputYear || (formData.JournalId && item.JournalId === formData.JournalId)) {
                            toast?.current?.show({
                                severity: 'warn',
                                summary: 'Cảnh báo trùng lặp',
                                detail: `Hệ thống phát hiện bài báo rất giống đã tồn tại: "${item.Title}". Vui lòng kiểm tra lại!`,
                                life: 8000
                            });
                            break;
                        }
                    }
                }
            }
        } catch (err) {
            console.error("Lỗi check trùng lặp:", err);
        }
    };

    const handleDoiExtraction = async () => {
        if (isReadOnlyForm) return;
        if (!doiInput || !doiInput.trim()) {
            toast?.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng nhập đường link hoặc mã DOI!', life: 3000 });
            return;
        }

        setIsExtracting(true);
        try {
            const userStr = localStorage.getItem('user');
            const currentUser = userStr ? JSON.parse(userStr) : null;
            const userId = currentUser ? currentUser.Id : '';

            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const response = await fetch(`${baseUrl}/extract-doi?doi=${encodeURIComponent(doiInput.trim())}&userId=${userId}`);

            if (!response.ok) {
                throw new Error('Không thể lấy dữ liệu từ DOI này. Vui lòng kiểm tra lại link hoặc nhập thủ công');
            }

            const result = await response.json();

            if (result.status === 'error') {
                throw new Error(result.message);
            }

            const metadata = result.data;
            let updateData = { ...formData };

            if (metadata.title) updateData.Title = metadata.title;
            if (metadata.publisher) updateData.Publisher = metadata.publisher;

            if (metadata.publication_date) {
                try {
                    const dateObj = new Date(metadata.publication_date);
                    if (!isNaN(dateObj.getTime())) {
                        updateData.PublicationDate = dateObj.toISOString().split('T')[0];
                    }
                } catch (e) { console.error("Lỗi convert ngày:", e); }
            } else if (metadata.year) {
                updateData.PublicationDate = `${metadata.year}-01-01`;
            }

            if (metadata.scholar_link) {
                updateData.ArticleUrl = metadata.scholar_link;
            } else if (metadata.publisher_link) {
                updateData.ArticleUrl = metadata.publisher_link;
            } else if (metadata.doi) {
                updateData.ArticleUrl = metadata.doi.startsWith('http') ? metadata.doi : `https://doi.org/${metadata.doi}`;
            }

            if (metadata.authors && Array.isArray(metadata.authors)) {
                const authorNames = metadata.authors.map(a => a.name || a.display_name).filter(Boolean);
                if (authorNames.length > 0) {
                    updateData.OriginalAuthors = authorNames.join(', ');
                    updateData.TotalAuthors = authorNames.length;

                    const normalizeVietnamese = (str) => {
                        if (!str) return '';
                        return str.toLowerCase()
                            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                            .replace(/đ/g, 'd').replace(/Đ/g, 'D')
                            .replace(/[^a-z0-9\s]/g, "")
                            .trim();
                    };

                    const matchUserByName = (authorName, usersList) => {
                        if (!authorName) return null;
                        const normAuthor = normalizeVietnamese(authorName);
                        const authorParts = normAuthor.split(/\s+/).filter(Boolean);
                        const sortedAuthor = [...authorParts].sort().join(' ');

                        for (let u of usersList) {
                            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
                            const normU = normalizeVietnamese(fullName);
                            const uParts = normU.split(/\s+/).filter(Boolean);
                            const sortedU = [...uParts].sort().join(' ');

                            if (sortedAuthor === sortedU) return u;

                            if (normU === normAuthor) return u;

                            const firstNameU = normalizeVietnamese(u.FirstName);
                            const lastNameU = normalizeVietnamese(u.LastName);

                            if (firstNameU && authorParts.includes(firstNameU)) {
                                const remainingAuthor = authorParts.filter(p => p !== firstNameU);
                                if (remainingAuthor.length === 0 || remainingAuthor.some(p => lastNameU.includes(p))) {
                                    return u;
                                }
                            }
                        }
                        return null;
                    };

                    const matchedMembers = [];

                    metadata.authors.forEach((authorData) => {
                        if (!authorData.name) return;
                        const authorName = authorData.name;

                        const matchedUser = matchUserByName(authorName, users);

                        const isFirst = authorData.author_position === 'first';
                        const isContact = authorData.is_corresponding === true;
                        const orgCount = parseInt(authorData.org_count) || 1;

                        if (matchedUser) {
                            if (!matchedMembers.some(m => m.UserId === matchedUser.Id)) {
                                matchedMembers.push({
                                    UserId: matchedUser.Id,
                                    FullName: `${matchedUser.LastName || ''} ${matchedUser.FirstName || ''}`.trim(),
                                    IsFirstAuthor: isFirst,
                                    IsContactAuthor: isContact,
                                    IsUD: true,
                                    OrganizationCount: orgCount,
                                    IsStudent: false,
                                    IsInstructor: true
                                });
                            }
                        } else {
                            matchedMembers.push({
                                UserId: '',
                                FullName: authorName,
                                IsFirstAuthor: isFirst,
                                IsContactAuthor: isContact,
                                IsUD: false,
                                OrganizationCount: orgCount,
                                IsStudent: false,
                                IsInstructor: true
                            });
                        }
                    });

                    if (!matchedMembers.some(m => m.IsContactAuthor) && matchedMembers.length > 0) {
                        matchedMembers[0].IsContactAuthor = true;
                    }

                    if (matchedMembers.length > 0) {
                        setMembers(matchedMembers);
                    }

                    const finalPrimaryCount = matchedMembers.filter(m => m.IsFirstAuthor || m.IsContactAuthor).length;
                    const finalContactCount = matchedMembers.filter(m => m.IsContactAuthor).length;

                    updateData.PrimaryAuthors = finalPrimaryCount > 0 ? finalPrimaryCount : 1;
                    updateData.ContactAuthors = finalContactCount > 0 ? finalContactCount : 1;
                }
            }

            let detectedQRanking = null;
            try {
                const cachedSci = sessionStorage.getItem('scimagoRankingDict');
                if (cachedSci && metadata.journal_name) {
                    const rankingDict = JSON.parse(cachedSci);
                    let rawName = metadata.journal_name.toLowerCase().replace(/[^a-z0-9]/g, '');
                    if (rankingDict[rawName]) {
                        detectedQRanking = rankingDict[rawName];
                    } else if (rawName.length > 10) {
                        const sortedKeys = Object.keys(rankingDict).sort((a, b) => b.length - a.length);
                        for (let key of sortedKeys) {
                            if (key.length > 10 && rawName.includes(key)) {
                                detectedQRanking = rankingDict[key];
                                break;
                            }
                        }
                    }
                }
            } catch (e) { }

            if (metadata.internal_journal_id) {
                updateData.JournalId = metadata.internal_journal_id;
                updateData.JournalName = metadata.internal_journal_name || metadata.journal_name;
                updateData.ISSN_ISBN = metadata.issn || '';
                updateData.JournalType = metadata.internal_journal_type || '';
                updateData.JournalScore = metadata.internal_journal_score || 0;
                updateData.JournalStatus = metadata.internal_journal_status || '';

                updateData.QRanking = detectedQRanking || metadata.internal_q_category || '';
                updateData.BonusCoefficient = '1.0';

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Ánh xạ Tạp chí thành công',
                    detail: `Hệ thống nhận diện: ${updateData.JournalName} (Điểm: ${updateData.JournalScore})`,
                    life: 5000
                });
            } else if (metadata.journal_name) {
                updateData.JournalName = metadata.journal_name;
                updateData.ISSN_ISBN = metadata.issn || '';
                updateData.JournalId = null;
                updateData.JournalType = detectedQRanking ? 'Tạp chí Quốc tế' : '';
                updateData.JournalScore = 0;
                updateData.JournalStatus = '';

                if (detectedQRanking) {
                    updateData.QRanking = detectedQRanking;
                    updateData.BonusCoefficient = '1.0';
                    toast?.current?.show({
                        severity: 'info',
                        summary: 'Phát hiện tạp chí SCImago',
                        detail: `Tạp chí này chưa có trong danh mục nội bộ, nhưng hệ thống nhận diện đây là tạp chí ${detectedQRanking}!`,
                        life: 6000
                    });
                } else {
                    toast?.current?.show({
                        severity: 'warn',
                        summary: 'Lưu ý',
                        detail: 'Đã lấy được tên tạp chí nhưng chưa ánh xạ được với danh mục nội bộ. Vui lòng nhấn "Chọn tạp chí"!',
                        life: 6000
                    });
                }
            }

            if (metadata.issn) {
                updateData.ScimagoUrl = `https://www.scimagojr.com/journalsearch.php?q=${metadata.issn.replace('-', '')}`;
                updateData.InternationalProofUrl = `https://mjl.clarivate.com/search-results?issn=${metadata.issn}&exact_match=true`;
            } else if (metadata.journal_name) {
                updateData.InternationalProofUrl = `https://mjl.clarivate.com/search-results?query=${encodeURIComponent(metadata.journal_name)}`;
            }

            if (metadata.volume) updateData.IssueNumber = metadata.volume;
            if (metadata.issue) updateData.IssueNumber = updateData.IssueNumber ? `${updateData.IssueNumber} (${metadata.issue})` : metadata.issue;

            if (metadata.first_page && metadata.last_page) {
                updateData.PageRange = `${metadata.first_page} - ${metadata.last_page}`;
            } else if (metadata.pages) {
                updateData.PageRange = metadata.pages;
            }

            setFormData(updateData);
            if (!metadata.internal_journal_id && !metadata.journal_name) {
                toast?.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã trích xuất dữ liệu tự động!', life: 3000 });
            }

        } catch (err) {
            console.error("Lỗi DOI:", err);
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: err.message, life: 5000 });
        } finally {
            setIsExtracting(false);
        }
    };

    const handleCalculateBonus = () => {
        if (isReadOnlyForm) return;

        let a_coeff = 1.0;
        let message = "Loại công trình tính hệ số mặc định (1.0)";

        const contactAuthors = members.filter(m => m.IsContactAuthor || m.isContactAuthor);
        const journalScore = parseFloat(formData.JournalScore) || 0;

        if (journalScore >= 0.75) {
            if (contactAuthors.length > 0) {
                const hasUD = contactAuthors.some(m => m.IsUD || m.isUD);
                const hasNonUD = contactAuthors.some(m => !(m.IsUD || m.isUD));

                const hasUDWithMultiOrg = contactAuthors.some(m => (m.IsUD || m.isUD) && parseInt(m.OrganizationCount || m.organizationCount || 1) >= 2);

                if (hasUD && hasNonUD) {
                    a_coeff = 1.5;
                    message = "Tác giả liên hệ có người thuộc và không thuộc ĐHĐN (a = 1.5)";
                } else if (hasUD && !hasNonUD) {
                    if (hasUDWithMultiOrg) {
                        a_coeff = 1.5;
                        message = "Tác giả liên hệ thuộc ĐHĐN nhưng có từ 2 đơn vị công tác trở lên (a = 1.5)";
                    } else {
                        a_coeff = 2.0;
                        message = "Tất cả tác giả liên hệ đều thuộc ĐHĐN (a = 2.0)";
                    }
                } else if (!hasUD && hasNonUD) {
                    a_coeff = 1.0;
                    message = "Tất cả tác giả liên hệ không thuộc ĐHĐN (a = 1.0)";
                }
            } else {
                a_coeff = 1.0;
                message = "Công trình từ 0.75 điểm trở lên nhưng không có tác giả liên hệ (a = 1.0)";
            }
        }
        else {
            a_coeff = 1.0;
            message = "Công trình có điểm dưới 0.75, hệ số tác giả mặc định (a = 1.0)";
        }

        setFormData({ ...formData, BonusCoefficient: a_coeff.toFixed(1).toString() });
        toast?.current?.show({ severity: 'success', summary: 'Tính hệ số', detail: message, life: 4000 });
    };

    const handlePreviewScores = () => {
        const journalScore = parseFloat(formData.JournalScore) || 0;
        const a_coeff = parseFloat(formData.BonusCoefficient) || 1.0;

        const tickedPrimaryCount = members.filter(m => m.IsFirstAuthor || m.IsContactAuthor).length;
        const primaryAuthors = Math.max(parseInt(formData.PrimaryAuthors) || 1, tickedPrimaryCount);
        const totalAuthors = Math.max(parseInt(formData.TotalAuthors) || 1, members.length);

        const totalScore = journalScore * a_coeff;

        let primaryScore = totalScore;
        let coAuthorScore = 0;

        if (totalAuthors > 1) {
            const thirdPool = totalScore / 3;
            const twoThirdsPool = (totalScore * 2) / 3;

            const primaryShareFromThird = thirdPool / primaryAuthors;
            const shareFromTwoThirds = twoThirdsPool / totalAuthors;

            primaryScore = primaryShareFromThird + shareFromTwoThirds;
            coAuthorScore = shareFromTwoThirds;
        }

        let totalStudentPoints = 0;
        let instructorCount = 0;

        if (totalAuthors > 1) {
            members.forEach(m => {
                const isStudent = m.IsStudent || m.isStudent;
                const isInstructor = m.IsInstructor || m.isInstructor;
                const isPrimary = m.IsFirstAuthor || m.isFirstAuthor || m.IsContactAuthor || m.isContactAuthor;

                const mScore = isPrimary ? primaryScore : coAuthorScore;

                if (isStudent) totalStudentPoints += mScore;
                if (isInstructor) instructorCount++;
            });
        }

        const currentUser = JSON.parse(localStorage.getItem('user'));
        let yourScore = 0;

        if (currentUser && members.length > 0) {
            const myIndex = members.findIndex(m => m.UserId?.toString() === currentUser.Id?.toString());
            if (myIndex !== -1) {
                const me = members[myIndex];

                if (me.IsFirstAuthor || me.IsContactAuthor) {
                    yourScore = primaryScore;
                } else {
                    yourScore = coAuthorScore;
                }

                const isMeInstructor = me.IsInstructor || me.isInstructor;
                if (isMeInstructor && instructorCount > 0 && journalScore >= 0.75) {
                    yourScore += (totalStudentPoints / instructorCount);
                }

                const orgCount = parseInt(me.OrganizationCount) || 1;
                if (orgCount > 1) {
                    yourScore = yourScore * 0.5;
                }
            } else {
                yourScore = "Bạn chưa có tên trong danh sách tác giả!";
            }
        } else {
            yourScore = "Vui lòng chọn thành viên!";
        }

        setPreviewScores({
            primary: primaryScore.toFixed(4),
            coAuthor: totalAuthors > 1 ? coAuthorScore.toFixed(4) : "0.0000",
            yourScore: typeof yourScore === 'number' ? yourScore.toFixed(4) : yourScore
        });
    };

    const addMember = () => {
        if (isReadOnlyForm) return;

        const newMembers = [...members, {
            UserId: '',
            FullName: '',
            Email: '',
            IsFirstAuthor: false,
            IsContactAuthor: false,
            IsUD: false,
            OrganizationCount: 1,
            IsStudent: false,
            IsInstructor: true
        }];
        setMembers(newMembers);

        if (newMembers.length > (parseInt(formData.TotalAuthors) || 0)) {
            setFormData(prev => ({ ...prev, TotalAuthors: newMembers.length }));
        }
    };

    const removeMember = (index) => {
        if (isReadOnlyForm) return;
        const newMembers = members.filter((_, i) => i !== index);
        setMembers(newMembers);
    };

    const handleMemberChange = (index, field, value) => {
        const updated = [...members];
        updated[index][field] = value;

        if (field === 'UserId' && value) {
            const selectedUser = users.find(u => u.Id === value);
            if (selectedUser) {
                updated[index].FullName = `${selectedUser.LastName || ''} ${selectedUser.FirstName || ''}`.trim();
                updated[index].IsUD = true;
            }
        }

        if (field === 'IsStudent' && value) {
            updated[index].UserId = '';
            updated[index].IsInstructor = false;
            updated[index].IsUD = true;
        }

        if (field === 'IsInstructor' && value) {
            updated[index].IsStudent = false;
        }

        setMembers(updated);

        if (field === 'IsFirstAuthor' || field === 'IsContactAuthor') {
            const tickedCount = updated.filter(m => m.IsFirstAuthor || m.IsContactAuthor).length;
            if (tickedCount > (parseInt(formData.PrimaryAuthors) || 0)) {
                setFormData(prev => ({ ...prev, PrimaryAuthors: tickedCount }));
            }
        }
    };

    const handleChange = (e) => {
        if (isReadOnlyForm) return;
        const { name, value, type, checked } = e.target;
        let finalValue = type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? '' : Number(value)) : value);

        if (name === 'PrimaryAuthors') {
            const tickedCount = members.filter(m => m.IsFirstAuthor || m.IsContactAuthor).length;
            if (finalValue !== '' && finalValue < tickedCount) {
                toast?.current?.show({ severity: 'warn', summary: 'Lưu ý', detail: `Số tác giả chính không được nhỏ hơn số người đã được tick (${tickedCount})!`, life: 3000 });
                finalValue = tickedCount;
            }
        }

        if (name === 'TotalAuthors') {
            if (finalValue !== '' && finalValue < members.length) {
                toast?.current?.show({ severity: 'warn', summary: 'Lưu ý', detail: `Tổng số tác giả không được nhỏ hơn số thành viên trong danh sách (${members.length})!`, life: 3000 });
                finalValue = members.length;
            }
        }

        setFormData({
            ...formData,
            [name]: finalValue
        });
    };

    const handleSelectJournal = (j) => {
        if (isReadOnlyForm) return;
        setFormData({
            ...formData,
            JournalId: j.Id,
            JournalName: j.Name,
            ISSN_ISBN: j.ISSN || j.ISSN_ISBN,
            JournalType: j.JournalTypeName || j.JournalType || '',
            JournalScore: j.Score || 0,
            JournalStatus: j.Status,
            BonusCoefficient: '1.0',
            QRanking: ''
        });
        setIsJournalModalOpen(false);
    };

    const handleMultiFileChange = (e) => {
        if (isReadOnlyForm) return;

        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
        const MAX_FILE_SIZE = 5 * 1024 * 1024;

        const allSelectedFiles = Array.from(e.target.files);
        if (allSelectedFiles.length === 0) return;

        const validFiles = allSelectedFiles.filter(file => {
            const isTypeValid = allowedTypes.includes(file.type);
            const fileName = file.name.toLowerCase();
            const isExtensionValid = allowedExtensions.some(ext => fileName.endsWith(ext));

            if (!isTypeValid && !isExtensionValid) {
                toast?.current?.show({ severity: 'warn', summary: 'Định dạng không hợp lệ', detail: `Tệp "${file.name}" không được hỗ trợ. Chỉ cho phép PDF, JPG/JPEG, PNG`, life: 5000 });
                return false;
            }

            if (file.size > MAX_FILE_SIZE) {
                toast?.current?.show({ severity: 'warn', summary: 'Dung lượng quá lớn', detail: `Tệp "${file.name}" vượt quá giới hạn 5MB. Vui lòng chọn tệp nhỏ hơn.`, life: 5000 });
                return false;
            }

            return true;
        });

        if (validFiles.length === 0) {
            e.target.value = null;
            return;
        }

        const newFilesArray = validFiles.map(file => ({
            fileObj: file,
            name: file.name,
            description: ''
        }));

        setPendingFiles(prev => [...prev, ...newFilesArray]);
        e.target.value = null;
    };

    const removeExistingFile = (fileId) => {
        if (isReadOnlyForm) return;
        setDeletedFileIds(prev => [...prev, fileId]);
        const updated = existingFiles.filter(f => f.fileId !== fileId && f.url !== fileId);
        setFormData({ ...formData, EvidenceFileName: JSON.stringify(updated) });
    };

    const removePendingFile = (index) => {
        if (isReadOnlyForm) return;
        setPendingFiles(pendingFiles.filter((_, i) => i !== index));
    };

    const downloadIndividualFile = (fileObj) => {
        let fileUrl = '';
        if (fileObj.fileId) {
            fileUrl = `${process.env.REACT_APP_API_BASE_URL}/downloadevidence?fileId=${fileObj.fileId}`;
        } else if (fileObj.url || fileObj.link || fileObj.URL || fileObj.data) {
            fileUrl = fileObj.url || fileObj.link || fileObj.URL || fileObj.data;
            if (fileUrl.startsWith('/due-bucket') || fileUrl.startsWith('/uploads')) {
                fileUrl = `https://data.tapchikhkt.vn${fileUrl}`;
            }
        }
        if (fileUrl) window.open(fileUrl, '_blank');
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (isReadOnlyForm) return;
        setIsSubmittingForm(true);

        const currentUser = JSON.parse(localStorage.getItem('user')) || {};
        const method = isEditing ? 'PUT' : 'POST';
        const payload = {
            ...formData,
            UserId: currentUser.Id,
            CreatedByEmail: currentUser.Email || '',
            MembersJSON: JSON.stringify(members),
            Id: isEditing ? formData.Id : 0
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticle`, {
                method,
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json().catch(() => ({ status: 'error' }));

            if (response.ok && result.status !== 'error') {
                const finalArticleId = result.articleId || formData.Id;

                if (deletedFileIds.length > 0) {
                    await Promise.all(deletedFileIds.map(fId => {
                        if (typeof fId === 'number') {
                            return fetch(`${process.env.REACT_APP_API_BASE_URL}/deleteevidence?fileId=${fId}`, { method: 'DELETE', headers: getAuthHeaders() });
                        }
                        return Promise.resolve();
                    }));
                }

                if (pendingFiles.length > 0) {
                    await Promise.all(pendingFiles.map(pf => {
                        const fd = new FormData();
                        fd.append('articleId', finalArticleId);
                        fd.append('description', pf.description);
                        fd.append('file', pf.fileObj);
                        return fetch(`${process.env.REACT_APP_API_BASE_URL}/uploadevidence`, { method: 'POST', headers: getAuthHeaders(), body: fd });
                    }));
                }

                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã lưu bài báo và tệp đính kèm!', life: 3000 });
                if (typeof fetchData === 'function') fetchData(true);
                onClose();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Không thể lưu', detail: result.message || 'Lưu bài báo thất bại!', life: 6000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsSubmittingForm(false);
        }
    };

    const userOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || u.email || '';
            const dept = u.DepartmentName || '';
            const displayLabel = email ? `${fullName} - ${email}` : fullName;
            const searchStr = superCleanString(`${fullName} ${email} ${dept}`);

            return {
                label: displayLabel,
                value: u.Id,
                searchKey: searchStr,
                fullName: fullName,
                email: email,
                dept: dept
            };
        });
    }, [users]);

    const userItemTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {option.email} {option.dept ? ` - ${option.dept}` : ''}
                </span>
            </div>
        );
    };

    if (!isOpen) return null;

    const filteredJournals = journals.filter(j =>
        j.Name?.toLowerCase().includes(searchJournal.toLowerCase()) ||
        (j.ISSN || j.ISSN_ISBN)?.includes(searchJournal)
    );

    return (
        <>
            <div className="modal-overlay" style={{ zIndex: 1000 }}>
                <div className="modal-box form-modal-box" style={{ maxHeight: '95vh', overflowY: 'auto' }}>

                    <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>
                            {isReadOnlyForm ? "Xem chi tiết Bài báo - Báo cáo" : (isEditing ? "Cập nhật Bài báo - Báo cáo" : "Thêm mới Bài báo - Báo cáo")}
                        </h3>
                        <button className="close-btn" onClick={onClose} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>
                            &times;
                        </button>
                    </div>

                    <div className="modal-body" style={{ padding: '25px' }}>

                        <div className="sa-doi-box">
                            <label className="sa-doi-label">Tự động điền dữ liệu từ DOI (Tùy chọn)</label>
                            <div className="sa-flex-row-small" style={{ display: 'flex', gap: '10px' }}>
                                <input
                                    type="text"
                                    className="form-input"
                                    style={{ flex: 1 }}
                                    value={doiInput}
                                    onChange={(e) => setDoiInput(e.target.value)}
                                    placeholder="Nhập đường link DOI"
                                    disabled={isExtracting || isReadOnlyForm}
                                />
                                <button
                                    type="button"
                                    onClick={handleDoiExtraction}
                                    className={`btn-sa-extract ${isExtracting ? 'processing' : 'active'}`}
                                    disabled={isExtracting || isReadOnlyForm}
                                    style={{ flexShrink: 0 }}
                                >
                                    {isExtracting ? (
                                        <><div className="spinner-mini"></div> Đang xử lý</>
                                    ) : (
                                        <><i className="fa-solid fa-file-import"></i> Trích xuất</>
                                    )}
                                </button>
                            </div>
                            <p className="sa-doi-note">* Hệ thống sẽ tự động điền các mục: Tiêu đề, Tác giả, Năm, Ngày công bố, URL, Volume/Issue... nếu tìm thấy</p>
                        </div>

                        <form id="scientificArticleForm" onSubmit={handleFormSubmit}>
                            <div className="sa-form-group">
                                <label className="sa-label">Tiêu đề bài báo *</label>
                                <input type="text" name="Title" className="form-input" value={formData.Title || ''} onChange={handleChange} onBlur={handleTitleBlur} required disabled={isReadOnlyForm} />
                            </div>

                            <div className="sa-form-group">
                                <label className="sa-label">Tóm tắt</label>
                                <textarea name="Summary" className="form-input" rows="3" value={formData.Summary || ''} onChange={handleChange} style={{ resize: 'vertical' }} disabled={isReadOnlyForm}></textarea>
                            </div>

                            <div className="sa-form-group-large">
                                <label className="sa-label">Từ khóa</label>
                                <input type="text" name="Keywords" className="form-input" value={formData.Keywords || ''} onChange={handleChange} disabled={isReadOnlyForm} />
                            </div>

                            <div className="sa-section-divider">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
                                    <label style={{ fontWeight: '600', color: '#1e293b', margin: 0 }}>Thông tin Tạp chí / Kỷ yếu hội thảo *</label>
                                    {!isReadOnlyForm && (
                                        <button type="button" onClick={() => setIsJournalModalOpen(true)} className="btn-sa-primary">
                                            <i className="fa-solid fa-list-check"></i> Chọn tạp chí từ danh mục
                                        </button>
                                    )}
                                </div>
                                <div className="sa-table-container">
                                    <table className="sa-table" style={{ minWidth: '600px' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ textalign: 'left' }}>Tạp chí</th>
                                                <th style={{ textalign: 'left' }}>ISSN/ISBN</th>
                                                <th style={{ textalign: 'left' }}>Loại tạp chí</th>
                                                <th className="text-center">Điểm công trình</th>
                                                <th className="text-center">Đã duyệt?</th>
                                                {!isReadOnlyForm && <th width="80px">Thao tác</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.JournalName ? (
                                                <tr>
                                                    <td className="font-bold">
                                                        {formData.JournalName}
                                                        {!formData.JournalId && <span className="text-warning">(Chưa ánh xạ)</span>}
                                                    </td>
                                                    <td>{formData.ISSN_ISBN}</td>
                                                    <td>{formData.JournalType}</td>
                                                    <td className="text-center font-bold">{formData.JournalScore}</td>
                                                    <td className="text-center">{formData.JournalStatus === 'Đã duyệt' && <i className="fa-solid fa-circle-check text-success"></i>}</td>
                                                    {!isReadOnlyForm && (
                                                        <td className="text-center">
                                                            <i className="fa-solid fa-trash-can text-danger" style={{ cursor: 'pointer' }} onClick={() => setFormData({ ...formData, JournalId: null, JournalName: '', ISSN_ISBN: '', JournalType: '', JournalScore: 0, JournalStatus: '' })}></i>
                                                        </td>
                                                    )}
                                                </tr>
                                            ) : (
                                                <tr>
                                                    <td colSpan={isReadOnlyForm ? "5" : "6"} className="sa-empty-state">
                                                        <i className="fa-solid fa-laptop-file sa-empty-icon"></i>
                                                        <div className="sa-empty-text">Chưa chọn tạp chí</div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="form-grid-2 sa-form-group">
                                <div className="form-group">
                                    <label className="sa-label">Danh mục tạp chí *</label>
                                    <select name="JournalCategory" className="form-input" value={formData.JournalCategory || ''} onChange={handleChange} required disabled={isReadOnlyForm}>
                                        <option value="">Chọn danh mục tạp chí</option>
                                        {journalCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="sa-label">Ngôn ngữ *</label>
                                    <select name="LanguageName" className="form-input" value={formData.LanguageName || ''} onChange={handleChange} required disabled={isReadOnlyForm}>
                                        <option value="">Chọn ngôn ngữ</option>
                                        {languageOptions.map(lang => <option key={lang.code} value={lang.name}>{lang.name}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="form-grid-2 sa-form-group">
                                <div className="form-group">
                                    <label className="sa-label">Lĩnh vực *</label>
                                    <select name="ResearchFieldId" className="form-input" value={formData.ResearchFieldId || ''} onChange={handleChange} required disabled={isReadOnlyForm}>
                                        <option value="">Chọn lĩnh vực</option>
                                        {fields.map(f => <option key={f.Id} value={f.Id}>{f.Name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="sa-label">Loại bài viết *</label>
                                    <select name="ArticleTypeId" className="form-input" value={formData.ArticleTypeId || ''} onChange={handleChange} required disabled={isReadOnlyForm}>
                                        <option value="">Chọn loại bài viết</option>
                                        {articleTypes.map(t => <option key={t.Id} value={t.Id}>{t.Name}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="sa-form-group">
                                <label className="sa-label">Số bài báo (Volume/Issue) *</label>
                                <input type="text" name="IssueNumber" className="form-input" value={formData.IssueNumber || ''} onChange={handleChange} required disabled={isReadOnlyForm} />
                            </div>

                            <div className="sa-flex-row sa-form-group" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                                <div style={{ flex: '1 1 200px' }}>
                                    <label className="sa-label">Từ trang - đến trang *</label>
                                    <input type="text" name="PageRange" className="form-input" value={formData.PageRange || ''} onChange={handleChange} required disabled={isReadOnlyForm} />
                                </div>
                                <div style={{ flex: '1 1 200px' }}>
                                    <label className="sa-label">Nhà xuất bản</label>
                                    <input type="text" name="Publisher" className="form-input" value={formData.Publisher || ''} onChange={handleChange} disabled={isReadOnlyForm} />
                                </div>
                                <div style={{ flex: '1 1 200px' }}>
                                    <label className="sa-label">Ngày công bố *</label>
                                    <Calendar
                                        id="PublicationDate" name="PublicationDate"
                                        value={formData.PublicationDate ? new Date(formData.PublicationDate) : null}
                                        dateFormat="dd/mm/yy" showIcon
                                        onChange={(e) => {
                                            const dateObj = e.value;
                                            let dateString = '';
                                            if (dateObj) {
                                                const yyyy = dateObj.getFullYear();
                                                const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
                                                const dd = String(dateObj.getDate()).padStart(2, '0');
                                                dateString = `${yyyy}-${mm}-${dd}`;
                                            }
                                            handleChange({ target: { name: 'PublicationDate', value: dateString, type: 'text' } });
                                        }}
                                        style={{ width: '100%' }}
                                        inputClassName="form-input"
                                        inputStyle={{ borderRadius: '6px 0 0 6px' }}
                                        disabled={isReadOnlyForm} required
                                    />
                                </div>
                            </div>

                            <div className="sa-form-group-large">
                                <label className="sa-label">Thuộc đề tài</label>
                                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <input type="checkbox" name="IsInsideProject" id="isInsideProject" checked={formData.IsInsideProject} onChange={handleChange} style={{ width: '18px', height: '18px' }} disabled={isReadOnlyForm} />
                                        <label htmlFor="isInsideProject" style={{ margin: 0, cursor: 'pointer' }}>Có thuộc đề tài</label>
                                    </div>
                                    <input type="text" name="ProjectCode" className="form-input" style={{ flex: '1 1 200px' }} placeholder="Mã đề tài" value={formData.ProjectCode || ''} onChange={handleChange} disabled={!formData.IsInsideProject || isReadOnlyForm} />
                                </div>
                            </div>

                            <div style={{ borderTop: '1px solid #eee', paddingTop: '20px' }}>
                                <p style={{ fontWeight: '600', color: '#333', marginBottom: '15px' }}>Thông tin tác giả</p>
                                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '15px' }}>
                                    <div style={{ flex: '1 1 150px' }}>
                                        <label className="sa-label-small">Tổng số tác giả *</label>
                                        <input type="number" name="TotalAuthors" className="form-input" value={formData.TotalAuthors || 1} onChange={handleChange} min="1" required disabled={isReadOnlyForm} />
                                    </div>
                                    <div style={{ flex: '1 1 150px' }}>
                                        <label className="sa-label-small">Số tác giả chính *</label>
                                        <input type="number" name="PrimaryAuthors" className="form-input" value={formData.PrimaryAuthors || 1} onChange={handleChange} min="1" required disabled={isReadOnlyForm} />
                                    </div>
                                    <div style={{ flex: '1 1 150px' }}>
                                        <label className="sa-label-small">Số tác giả liên hệ *</label>
                                        <input type="number" name="ContactAuthors" className="form-input" value={formData.ContactAuthors || 1} onChange={handleChange} min="1" required disabled={isReadOnlyForm} />
                                    </div>
                                </div>
                                <p className="sa-note-text">Lưu ý: Nếu tác giả vừa đứng đầu vừa là tác giả liên hệ thì tính là 1 cho cột Số tác giả chính</p>

                                <label className="sa-table-title">DANH SÁCH THÀNH VIÊN THỰC HIỆN THUỘC TRƯỜNG *</label>

                                <button type="button" onClick={addMember} disabled={isReadOnlyForm} className={`btn-sa-add-user ${isReadOnlyForm ? 'disabled' : 'active'}`}>
                                    <i className="fa-solid fa-user-plus"></i> Chọn người dùng hoặc thêm Sinh viên
                                </button>

                                <div style={{ overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                    <table className="custom-table" style={{ width: '100%', fontSize: '13px', backgroundColor: '#fff', border: 'none', minWidth: '1200px', margin: 0 }}>
                                        <thead>
                                            <tr>
                                                <th width="5%" style={{ textAlign: 'center' }}>STT</th>
                                                <th width="25%" style={{ textAlign: 'left' }}>Họ tên</th>
                                                <th width="9%" style={{ textAlign: 'center' }}>Tác giả đầu</th>
                                                <th width="10%" style={{ textAlign: 'center' }}>Tác giả liên hệ</th>
                                                <th width="10%" style={{ textAlign: 'center' }}>Thuộc ĐHĐN</th>
                                                <th width="14%" style={{ textAlign: 'center' }}>Số lượng CQ công tác</th>
                                                <th width="9%" style={{ textAlign: 'center' }}>Là Sinh viên</th>
                                                <th width="13%" style={{ textAlign: 'center' }}>Là GV Hướng dẫn</th>
                                                {!isReadOnlyForm && <th width="5%" style={{ textAlign: 'center' }}>Xóa</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {members.map((m, idx) => (
                                                <tr key={idx}>
                                                    <td className="text-center">{idx + 1}</td>
                                                    <td>
                                                        {(m.IsStudent || m.isStudent) ? (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                <input
                                                                    type="text"
                                                                    className="form-input"
                                                                    placeholder="Nhập họ tên Sinh viên"
                                                                    value={m.FullName || m.fullName || ''}
                                                                    onChange={(e) => handleMemberChange(idx, 'FullName', e.target.value)}
                                                                    disabled={isReadOnlyForm}
                                                                    style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
                                                                    required
                                                                />
                                                                <input
                                                                    type="email"
                                                                    className="form-input"
                                                                    placeholder="<Mã sinh viên>@due.udn.vn"
                                                                    value={m.Email || m.email || ''}
                                                                    onChange={(e) => handleMemberChange(idx, 'Email', e.target.value)}
                                                                    disabled={isReadOnlyForm}
                                                                    pattern="^[a-zA-Z0-9._%+-]+@due\.udn\.vn$"
                                                                    title="Vui lòng nhập đúng định dạng email đuôi @due.udn.vn"
                                                                    style={{ width: '100%', padding: '8px', boxSizing: 'border-box', border: '1px solid #3498db', backgroundColor: '#f0f8ff' }}
                                                                    required
                                                                />
                                                                <span style={{ fontSize: '11px', color: '#e74c3c', fontStyle: 'italic', lineHeight: '1.2' }}>
                                                                    * Nhập chính xác Email để hệ thống liên kết Mã sinh viên
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                <SearchableFocusDropdown
                                                                    value={m.UserId}
                                                                    options={userOptions}
                                                                    onChange={(e) => handleMemberChange(idx, 'UserId', e.value)}
                                                                    placeholder="Chọn GV nội bộ"
                                                                    panelUniqueClass={`custom-dropdown-panel-member-${idx}`}
                                                                    disabled={isReadOnlyForm}
                                                                    itemTemplate={userItemTemplate}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    className="form-input"
                                                                    placeholder="Hoặc nhập GV ngoài"
                                                                    value={m.FullName || m.fullName || ''}
                                                                    onChange={(e) => handleMemberChange(idx, 'FullName', e.target.value)}
                                                                    disabled={isReadOnlyForm}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '8px',
                                                                        boxSizing: 'border-box',
                                                                        backgroundColor: m.UserId ? '#f1f5f9' : '#fff'
                                                                    }}
                                                                    title="Nhập tên nếu là GV ngoài trường không có trong hệ thống"
                                                                />
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="text-center"><input type="checkbox" checked={m.IsFirstAuthor} onChange={(e) => handleMemberChange(idx, 'IsFirstAuthor', e.target.checked)} disabled={isReadOnlyForm} /></td>
                                                    <td className="text-center"><input type="checkbox" checked={m.IsContactAuthor} onChange={(e) => handleMemberChange(idx, 'IsContactAuthor', e.target.checked)} disabled={isReadOnlyForm} /></td>
                                                    <td className="text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={m.IsUD || m.isUD || false}
                                                            onChange={(e) => handleMemberChange(idx, 'IsUD', e.target.checked)}
                                                            disabled={isReadOnlyForm}
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <input type="number" className="form-input" style={{ width: '60px', textalign: 'center' }} value={m.OrganizationCount} onChange={(e) => handleMemberChange(idx, 'OrganizationCount', e.target.value)} min="1" disabled={isReadOnlyForm} />
                                                    </td>
                                                    <td className="text-center">
                                                        <input type="checkbox" checked={m.IsStudent || m.isStudent || false}
                                                            onChange={(e) => handleMemberChange(idx, 'IsStudent', e.target.checked)}
                                                            disabled={isReadOnlyForm} />
                                                    </td>
                                                    <td className="text-center">
                                                        <input type="checkbox" checked={m.IsInstructor || m.isInstructor || false}
                                                            onChange={(e) => handleMemberChange(idx, 'IsInstructor', e.target.checked)}
                                                            disabled={isReadOnlyForm} />
                                                    </td>
                                                    {!isReadOnlyForm && (
                                                        <td className="text-center">
                                                            <button type="button" onClick={() => removeMember(idx)} className="btn-sa-outline-danger">
                                                                <i className="fa-solid fa-trash-can"></i>
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginTop: '20px', marginBottom: '25px' }}>
                                <div style={{ flex: '2 1 250px' }}>
                                    <label className="sa-label">Danh sách tác giả nguyên bản *</label>
                                    <input type="text" name="OriginalAuthors" className="form-input" value={formData.OriginalAuthors || ''} onChange={handleChange} required disabled={isReadOnlyForm} />
                                </div>
                                <div style={{ flex: '1 1 120px' }}>
                                    <label className="sa-label">Xếp hạng (Q-Ranking)</label>
                                    <select name="QRanking" className="form-input" value={formData.QRanking || ''} onChange={handleChange} disabled={isReadOnlyForm}>
                                        <option value="">Không có / Chưa xét</option>
                                        <option value="Q1">Q1</option>
                                        <option value="Q2">Q2</option>
                                        <option value="Q3">Q3</option>
                                        <option value="Q4">Q4</option>
                                    </select>
                                </div>
                                <div style={{ flex: '1 1 180px' }}>
                                    <label className="sa-label">Hệ số tác giả *</label>
                                    <div style={{ display: 'flex', gap: '10px', height: '40px' }}>
                                        <input type="text" name="BonusCoefficient" className="form-input text-center font-bold" style={{ width: '80px' }} value={formData.BonusCoefficient || '1.0'} onChange={handleChange} disabled={isReadOnlyForm} />
                                        {!isReadOnlyForm && <button type="button" onClick={handleCalculateBonus} className="btn-sa-outline-primary">Tính hệ số</button>}
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: '25px' }}>
                                <button type="button" onClick={handlePreviewScores} className="btn-sa-outline-info">Xem trước điểm</button>
                                {previewScores && (
                                    <table className="sa-table" style={{ marginTop: '10px' }}>
                                        <tbody>
                                            <tr><td className="font-bold text-center" style={{ width: '50%' }}>Điểm của bạn</td><td className="text-center">{previewScores.yourScore}</td></tr>
                                            <tr><td className="font-bold text-center">Điểm tác giả chính</td><td className="text-center">{previewScores.primary}</td></tr>
                                            <tr><td className="font-bold text-center">Điểm đồng tác giả</td><td className="text-center">{previewScores.coAuthor}</td></tr>
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="sa-form-group"><label className="sa-label">Liên kết đến bài báo (URL / DOI URL)</label><input type="text" name="ArticleUrl" className="form-input" value={formData.ArticleUrl || ''} onChange={handleChange} disabled={isReadOnlyForm} /></div>
                            <div className="sa-form-group"><label className="sa-label">Link xếp loại SCIMAGOJR (Nếu có)</label><input type="text" name="ScimagoUrl" className="form-input" value={formData.ScimagoUrl || ''} onChange={handleChange} disabled={isReadOnlyForm} /></div>
                            <div className="sa-form-group-large"><label className="sa-label">Minh chứng tạp chí uy tín (SCIE/Scopus/Web of Science...)</label><input type="text" name="InternationalProofUrl" className="form-input" value={formData.InternationalProofUrl || ''} onChange={handleChange} disabled={isReadOnlyForm} /></div>

                            <div className="sa-form-group-large">
                                <label className="sa-label">Tệp tin minh chứng (Toàn văn bài báo)</label>

                                <p className="sa-note-text" style={{ color: '#e74c3c', fontStyle: 'italic', marginBottom: '15px' }}>
                                    * Chú ý: Hệ thống chỉ nhận tệp minh chứng ở định dạng PDF, JPG/JPEG và PNG. Dung lượng tối đa 5MB / tệp
                                </p>

                                {(existingFiles.length > 0 || pendingFiles.length > 0) && (
                                    <div className="sa-table-container">
                                        <table className="sa-table" style={{ minWidth: '400px', marginBottom: '15px' }}>
                                            <thead>
                                                <tr><th width="50px" className="text-center">TT</th><th>Mô tả</th><th width="35%">Minh chứng</th>{!isReadOnlyForm && <th width="80px" className="text-center">Xóa</th>}</tr>
                                            </thead>
                                            <tbody>
                                                {existingFiles.map((file, idx) => (
                                                    <tr key={`ext-${idx}`}>
                                                        <td className="text-center">{idx + 1}</td>
                                                        <td><span style={{ fontSize: '13px', color: '#666' }}>{file.description || 'Không có mô tả'}</span></td>
                                                        <td><span onClick={() => downloadIndividualFile(file)} style={{ color: '#3498db', cursor: 'pointer', wordBreak: 'break-all' }} title="Nhấn để tải về">{file.name}</span></td>
                                                        {!isReadOnlyForm && (
                                                            <td className="text-center">
                                                                <button type="button" onClick={() => removeExistingFile(file.fileId || file.url)} className="btn-sa-outline-danger"><i className="fa-solid fa-trash-can"></i></button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                                {pendingFiles.map((file, idx) => (
                                                    <tr key={`pend-${idx}`}>
                                                        <td className="text-center">{existingFiles.length + idx + 1}</td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className="form-input"
                                                                placeholder="Nhập mô tả"
                                                                value={file.description || ''}
                                                                onChange={(e) => {
                                                                    const newFiles = [...pendingFiles];
                                                                    newFiles[idx].description = e.target.value;
                                                                    setPendingFiles(newFiles);
                                                                }}
                                                                style={{ border: 'none', background: 'transparent', borderBottom: '1px solid #eee', borderRadius: 0 }}
                                                                disabled={isReadOnlyForm}
                                                            />
                                                        </td>
                                                        <td><span style={{ color: '#2ecc71', wordBreak: 'break-all' }}>{file.name} <i className="fa-solid fa-cloud-arrow-up"></i> (Mới)</span></td>
                                                        {!isReadOnlyForm && (
                                                            <td className="text-center">
                                                                <button type="button" onClick={() => removePendingFile(idx)} className="btn-sa-outline-danger"><i className="fa-solid fa-trash-can"></i></button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {!isReadOnlyForm && (
                                    <div className="sa-file-zone">
                                        <input
                                            type="file"
                                            multiple
                                            accept=".pdf, .jpg, .jpeg, .png, application/pdf, image/jpeg, image/png"
                                            onChange={handleMultiFileChange}
                                            className="sa-file-input"
                                            title="Nhấn để chọn tệp"
                                        />

                                        {(existingFiles.length > 0 || pendingFiles.length > 0) ? (
                                            <>
                                                <i className="fa-solid fa-cloud-arrow-up sa-file-icon"></i>
                                                <p className="sa-file-text">Đã chọn {existingFiles.length + pendingFiles.length} tệp minh chứng</p>
                                                <button type="button" className="sa-btn-upload-fake">Thêm tệp tin khác</button>
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa-solid fa-cloud-arrow-up sa-file-icon" style={{ fontSize: '40px' }}></i>
                                                <p className="sa-file-text">Chưa có tệp nào được chọn</p>
                                                <button type="button" className="sa-btn-upload-fake">Chọn tệp từ thiết bị</button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>

                    <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 25px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        {isReadOnlyForm ? (
                            <button type="button" className="btn-submit" onClick={onClose}>Đóng</button>
                        ) : (
                            <>
                                <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmittingForm}>Hủy</button>
                                <button type="submit" form="scientificArticleForm" className="btn-submit" disabled={isSubmittingForm}>
                                    {isSubmittingForm ? <><i className="fa-solid fa-spinner fa-spin"></i> Đang lưu...</> : "Hoàn tất"}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {isJournalModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-box form-modal-box" style={{ width: '90%', maxWidth: '1200px', maxHeight: '90vh' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Danh sách tạp chí</h3>
                            <button className="close-btn" onClick={() => setIsJournalModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                                <input
                                    type="text"
                                    placeholder="Tìm tên tạp chí, ISSN"
                                    className="form-input"
                                    style={{ flex: 1, padding: '12px' }}
                                    value={searchJournal}
                                    onChange={(e) => setSearchJournal(e.target.value)}
                                />
                                {!isReadOnlyForm && (
                                    <button
                                        type="button"
                                        onClick={() => setIsCreateJournalModalOpen(true)}
                                        style={{
                                            backgroundColor: '#1a9953',
                                            color: '#fff',
                                            border: 'none',
                                            padding: '0 20px',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontWeight: '600',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        <i className="fa-solid fa-plus"></i> Thêm mới
                                    </button>
                                )}
                            </div>
                            <div style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'auto' }}>
                                <table className="custom-table" style={{ minWidth: '1000px', width: '100%' }}>
                                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                                        <tr>
                                            <th width="50px">STT</th>
                                            <th>TẠP CHÍ</th>
                                            <th>ISSN/ISBN</th>
                                            <th>LOẠI TẠP CHÍ</th>
                                            <th style={{ textalign: 'center' }}>ĐIỂM CÔNG TRÌNH</th>
                                            <th style={{ textalign: 'center' }}>ĐÃ DUYỆT?</th>
                                            <th width="100px">CHỨC NĂNG</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredJournals.map((j, idx) => (
                                            <tr key={j.Id}>
                                                <td style={{ textalign: 'center' }}>{idx + 1}</td>
                                                <td style={{ fontWeight: 'bold' }}>{j.Name}</td>
                                                <td>{j.ISSN || j.ISSN_ISBN}</td>
                                                <td>{j.JournalTypeName || j.JournalType}</td>
                                                <td style={{ textalign: 'center', fontWeight: 'bold' }}>{j.Score}</td>
                                                <td style={{ textalign: 'center' }}>
                                                    {j.Status === 'Đã duyệt' && <i className="fa-solid fa-circle-check" style={{ color: '#2ecc71' }}></i>}
                                                </td>
                                                <td style={{ textalign: 'center' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSelectJournal(j)}
                                                        style={{
                                                            padding: '5px 15px',
                                                            backgroundColor: '#fff',
                                                            border: '1px solid #2ecc71',
                                                            color: '#2ecc71',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <i className="fa-solid fa-check"></i> Chọn
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 25px' }}>
                            <button type="button" className="btn-cancel" onClick={() => setIsJournalModalOpen(false)}>Quay về</button>
                        </div>
                    </div>
                </div>
            )}

            <JournalForm
                isOpen={isCreateJournalModalOpen}
                onClose={() => { setIsCreateJournalModalOpen(false); setJournalFormData(initialJournalForm); }}
                onSubmit={handleCreateJournalSubmit}
                formData={journalFormData}
                setFormData={setJournalFormData}
                isEditing={false}
                countries={countries}
                journalTypes={journalTypes}
                toast={toast}
                zIndex={3000}
            />
        </>
    );
};

export default ScientificArticleForm;